import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * O nome SIDEB que se desmonta — usado na página 404.
 *
 * As peças são as mesmas do /LogoNome.svg (sem o "+"). Cada uma traz a
 * caixa já medida (x, y, largura, altura), para a física não depender de
 * getBBox. Ao aparecer na tela, o nome treme e cai letra por letra
 * (S → I → D → E → B); depois dá para chutar as peças e remontar o logo.
 */
const PECAS = [
    // S
    { letra: 0, cor: '#2E4974', caixa: [60.033, 0, 52, 54], d: 'M112.033 0H60.0327C65.8105 40.3212 80.2549 50.8212 112.033 54V0Z' },
    { letra: 0, cor: '#11993C', caixa: [60.033, 54, 52, 108], d: 'M60.0327 54.032V162C89.4031 159.501 111.07 140.506 112.033 108.016C112.033 75.5257 84.1068 53.0323 60.0327 54.032Z' },
    { letra: 0, cor: '#E83A3C', caixa: [0, 108, 52, 54], d: 'M0 162H52C50.5556 132.5 31.7778 108 0 108V162Z' },
    { letra: 0, cor: '#F9B048', caixa: [0.033, 0, 52, 108], d: 'M52.0327 0.178528V108C23.8859 108 0.0327148 83.0413 0.0327148 54.0893C0.0327148 21.643 27.2254 -2.31735 52.0327 0.178528Z' },
    // I
    { letra: 1, cor: '#0974BA', caixa: [144.033, 72, 40, 90] },
    { letra: 1, cor: '#2E4974', caixa: [144.033, 0, 40, 54], d: 'M144.033 40C144.033 17.9086 161.942 0 184.033 0V0V54H144.033V40Z' },
    // D
    { letra: 2, cor: '#0974BA', caixa: [264.033, 0, 69, 162], d: 'M264.033 0.0480557V162C303.005 158.251 331.755 129.76 333.033 81.024C333.033 32.2885 295.978 -1.45151 264.033 0.0480557Z' },
    { letra: 2, cor: '#E83A3C', caixa: [216.033, 104, 40, 58] },
    { letra: 2, cor: '#11993C', caixa: [216.033, 0, 40, 104] },
    // E
    { letra: 3, cor: '#0974BA', caixa: [365.033, 0, 95, 35], d: 'M460.033 0V0C460.033 19.33 444.363 35 425.033 35H365.033V0H460.033Z' },
    { letra: 3, cor: '#11993C', caixa: [365.033, 127, 95, 35], d: 'M425.033 127C444.363 127 460.033 142.67 460.033 162V162H365.033V127H425.033Z' },
    { letra: 3, cor: '#E83A3C', caixa: [365.033, 63, 80, 35], d: 'M427.533 63C437.198 63 445.033 70.835 445.033 80.5V80.5C445.033 90.165 437.198 98 427.533 98H365.033V63H427.533Z' },
    { letra: 3, cor: '#F9B048', caixa: [365.033, 0, 40, 162] },
    // B
    { letra: 4, cor: '#0974BA', caixa: [540.033, 0, 35, 69], d: 'M540.033 0.0204681V69C559.802 67.4033 574.385 55.268 575.033 34.5102C575.033 13.7525 556.237 -0.618236 540.033 0.0204681Z' },
    { letra: 4, cor: '#E83A3C', caixa: [540.033, 69, 55, 93], d: 'M540.033 69.0276V162C571.098 159.848 594.015 143.492 595.033 115.514C595.033 87.536 565.496 68.1667 540.033 69.0276Z' },
    { letra: 4, cor: '#11993C', caixa: [492.033, 0, 40, 69] },
    { letra: 4, cor: '#F9B048', caixa: [492.033, 69, 40, 93] },
];

// Palco no sistema de coordenadas do logo: o nome (595 de largura) fica
// centralizado entre as paredes, e o chão fica abaixo dele.
const PAREDE_ESQ = -90;
const PAREDE_DIR = 686;
const CHAO = 270;
const GRAVIDADE = 0.55;
const TOPO = 24;

const sorteio = (amplitude) => (Math.random() - 0.5) * amplitude;

function prefereMenosMovimento() {
    return typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;
}

function LogoDesmontando() {
    const svgRef = useRef(null);
    const grupoRef = useRef(null);
    const pecasRef = useRef([]);
    const corpos = useRef(PECAS.map(({ letra, caixa: [x, y, w, h] }) => ({
        letra, w, h, cx: x + w / 2, cy: y + h / 2,
        x: 0, y: 0, a: 0, vx: 0, vy: 0, va: 0, espera: 0,
    })));
    const modo = useRef('montado'); // 'montado' | 'caindo' | 'remontando'
    const quadro = useRef(0);
    const timers = useRef([]);
    // 'intro' enquanto o nome ainda vai cair; 'caido' e 'montado' depois disso
    const [fase, setFase] = useState('intro');

    const desenhar = useCallback(() => {
        corpos.current.forEach((c, i) => {
            const el = pecasRef.current[i];
            if (el) el.setAttribute('transform', `translate(${c.x.toFixed(2)} ${c.y.toFixed(2)}) rotate(${c.a.toFixed(2)} ${c.cx} ${c.cy})`);
        });
    }, []);

    // Um passo da física; devolve se ainda tem peça se mexendo.
    const passo = useCallback(() => {
        let mexendo = false;
        for (const c of corpos.current) {
            if (c.espera > 0) { c.espera--; mexendo = true; continue; }
            c.vy += GRAVIDADE; c.x += c.vx; c.y += c.vy; c.a += c.va;
            const r = (c.a * Math.PI) / 180;
            const cos = Math.abs(Math.cos(r)), sen = Math.abs(Math.sin(r));
            const meiaL = (c.w * cos + c.h * sen) / 2, meiaA = (c.w * sen + c.h * cos) / 2;
            const cx = c.cx + c.x, cy = c.cy + c.y;
            if (cy + meiaA > CHAO) {
                c.y -= cy + meiaA - CHAO;
                if (c.vy > 0) c.vy *= -0.4;
                if (Math.abs(c.vy) < 1.2) c.vy = 0;
                c.vx *= 0.8; c.va *= 0.7;
                // no chão, a peça deita no lado mais próximo
                c.a += (Math.round(c.a / 90) * 90 - c.a) * 0.2;
            }
            if (cx - meiaL < PAREDE_ESQ) { c.x += PAREDE_ESQ - (cx - meiaL); c.vx = Math.abs(c.vx) * 0.6; }
            if (cx + meiaL > PAREDE_DIR) { c.x -= cx + meiaL - PAREDE_DIR; c.vx = -Math.abs(c.vx) * 0.6; }
            if (Math.abs(c.vx) > 0.05 || Math.abs(c.vy) > 0.05 || Math.abs(c.va) > 0.05) mexendo = true;
        }
        return mexendo;
    }, []);

    const animar = useCallback(() => {
        if (quadro.current) return;
        const loop = () => {
            const mexendo = passo();
            desenhar();
            quadro.current = mexendo && modo.current === 'caindo' ? requestAnimationFrame(loop) : 0;
        };
        quadro.current = requestAnimationFrame(loop);
    }, [passo, desenhar]);

    const derrubar = useCallback((porLetra) => {
        modo.current = 'caindo';
        corpos.current.forEach((c) => {
            if (porLetra) {
                c.vx = sorteio(4); c.vy = -1 - Math.random() * 3;
                c.espera = c.letra * 18 + Math.floor(Math.random() * 7);
            } else {
                c.vx = sorteio(7); c.vy = -2 - Math.random() * 6;
                c.espera = Math.floor(Math.random() * 22);
            }
            c.va = sorteio(14);
        });
        setFase('caido');
        if (prefereMenosMovimento()) {
            for (let i = 0; i < 600; i++) passo();
            desenhar();
            return;
        }
        animar();
    }, [animar, passo, desenhar]);

    const remontar = useCallback(() => {
        cancelAnimationFrame(quadro.current);
        quadro.current = 0;
        const inicio = corpos.current.map((c) => ({ x: c.x, y: c.y, a: c.a }));
        const fim = () => {
            corpos.current.forEach((c) => { c.x = c.y = c.a = c.vx = c.vy = c.va = 0; });
            desenhar();
            modo.current = 'montado';
            setFase('montado');
        };
        if (prefereMenosMovimento()) { fim(); return; }
        modo.current = 'remontando';
        const t0 = performance.now();
        const loop = (agora) => {
            let terminou = true;
            corpos.current.forEach((c, i) => {
                const t = Math.min(1, Math.max(0, (agora - t0 - i * 35) / 750));
                if (t < 1) terminou = false;
                const k = 1 + 2.2 * (t - 1) ** 3 + 1.2 * (t - 1) ** 2; // easeOutBack
                c.x = inicio[i].x * (1 - k); c.y = inicio[i].y * (1 - k); c.a = inicio[i].a * (1 - k);
            });
            desenhar();
            if (terminou) { quadro.current = 0; fim(); } else quadro.current = requestAnimationFrame(loop);
        };
        quadro.current = requestAnimationFrame(loop);
    }, [desenhar]);

    // Treme o nome inteiro por `duracao` ms e depois chama `depois`.
    const tremer = useCallback((duracao, depois) => {
        const grupo = grupoRef.current;
        const t0 = performance.now();
        const loop = (agora) => {
            const t = (agora - t0) / duracao;
            if (t >= 1 || !grupo) {
                grupo?.setAttribute('transform', `translate(0 ${TOPO})`);
                depois();
                return;
            }
            const forca = 1 - t;
            const dx = Math.sin(t * 40) * 5 * forca, giro = Math.sin(t * 31) * 0.6 * forca;
            grupo.setAttribute('transform', `translate(${dx.toFixed(2)} ${TOPO}) rotate(${giro.toFixed(2)} 297 81)`);
            requestAnimationFrame(loop);
        };
        requestAnimationFrame(loop);
    }, []);

    // Só desmonta quando o nome está de fato na tela e a aba está aberta.
    useEffect(() => {
        const svg = svgRef.current;
        const agendar = (fn, ms) => timers.current.push(setTimeout(fn, ms));
        let comecou = false;
        let ativo = true;
        let naTela = typeof IntersectionObserver === 'undefined';

        const comecar = () => {
            if (comecou || !naTela || document.visibilityState === 'hidden') return;
            comecou = true;
            if (prefereMenosMovimento()) { derrubar(false); return; }
            const cair = () => { if (ativo) derrubar(true); };
            agendar(() => tremer(500, () => ativo && agendar(() => tremer(450, cair), 120)), 1300);
        };

        const observador = typeof IntersectionObserver === 'undefined' ? null
            : new IntersectionObserver((entradas) => {
                naTela = entradas.some((e) => e.isIntersecting);
                comecar();
            }, { threshold: 0.6 });
        if (observador && svg) observador.observe(svg);
        document.addEventListener('visibilitychange', comecar);
        comecar();

        const pendentes = timers.current;
        return () => {
            ativo = false;
            observador?.disconnect();
            document.removeEventListener('visibilitychange', comecar);
            pendentes.forEach(clearTimeout);
            cancelAnimationFrame(quadro.current);
            quadro.current = 0;
        };
    }, [derrubar, tremer]);

    const chutar = (i) => {
        if (modo.current === 'remontando' || fase === 'intro') return;
        if (modo.current === 'montado') { derrubar(false); return; }
        const c = corpos.current[i];
        c.vy = -9 - Math.random() * 5; c.vx = sorteio(9); c.va = sorteio(24); c.espera = 0;
        if (!prefereMenosMovimento()) animar();
    };

    const dica = {
        intro: 'Hmm, tem uma peça meio solta aqui…',
        caido: 'Clique nas peças pra chutar.',
        montado: 'Pronto, inteiro de novo.',
    }[fase];

    return (
        <div>
            <svg
                ref={svgRef}
                viewBox={`${PAREDE_ESQ} 0 ${PAREDE_DIR - PAREDE_ESQ} 300`}
                className="block w-full h-auto select-none touch-manipulation"
                role="img"
                aria-label="Logo do SIDEB caindo em pedaços"
            >
                <line
                    x1={PAREDE_ESQ + 6} y1={CHAO + TOPO + 2} x2={PAREDE_DIR - 6} y2={CHAO + TOPO + 2}
                    className="stroke-slate-200 dark:stroke-slate-700"
                    strokeWidth="3" strokeLinecap="round"
                />
                <g ref={grupoRef} transform={`translate(0 ${TOPO})`}>
                    {PECAS.map((p, i) => (
                        <g
                            key={i}
                            ref={(el) => { pecasRef.current[i] = el; }}
                            onPointerDown={() => chutar(i)}
                            className={fase === 'intro' ? undefined : 'cursor-pointer'}
                        >
                            {p.d
                                ? <path d={p.d} fill={p.cor} />
                                : <rect x={p.caixa[0]} y={p.caixa[1]} width={p.caixa[2]} height={p.caixa[3]} fill={p.cor} />}
                        </g>
                    ))}
                </g>
            </svg>
            <div className="mt-3 flex flex-wrap items-center justify-end gap-x-3 gap-y-1 text-sm text-slate-500 dark:text-slate-400">
                <span aria-live="polite">{dica}</span>
                {fase !== 'intro' && (
                    <button
                        type="button"
                        onClick={() => (fase === 'montado' ? derrubar(false) : remontar())}
                        className="font-semibold text-[#0974BA] underline underline-offset-4 hover:text-[#0974BA]/80"
                    >
                        {fase === 'montado' ? 'Derrubar de novo' : 'Montar o logo de novo'}
                    </button>
                )}
            </div>
        </div>
    );
}

export default LogoDesmontando;
